// Renders a stroke-event recording to a 2D canvas. Used by GuessDrawing
// (snap, animated: false) and the playback screen / prototype (animated: true).
//
// Recording shape:
//   { v: 1, w: 500, h: 500, events: [
//       { t: 'stroke', ts: <ms>, c: '#FFFFFF', s: 10, pts: [[x,y], ...] },
//       { t: 'undo',   ts: <ms> },
//       { t: 'clear',  ts: <ms> },
//       ...
//   ] }
//
// Animated mode scales the recording's relative timestamps so the whole
// playback fits options.targetMs. Undo and clear get fixed-duration scrub
// animations regardless of scaling so they're always visible.

const UNDO_MS = 200;
const CLEAR_MS = 200;
// Animation duration of a normal stroke is bounded so very fast scribbles
// don't blink and very slow strokes don't drag.
const STROKE_MIN_MS = 35;
const STROKE_MAX_MS = 900;
// Fixed breath between events so each stroke reads as a unit.
const EVENT_GAP_MS = 50;

const sleep = (ms, signal) => new Promise((resolve) => {
    if (signal.cancelled) return resolve();
    const id = setTimeout(resolve, ms);
    signal.onCancel(() => { clearTimeout(id); resolve(); });
});

const drawPolyline = (ctx, points, upToIndex) => {
    if (points.length === 0 || upToIndex < 1) return;
    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i <= upToIndex && i < points.length; i++) {
        ctx.lineTo(points[i][0], points[i][1]);
    }
    ctx.stroke();
};

const repaintScene = (ctx, w, h, visible) => {
    ctx.clearRect(0, 0, w, h);
    for (const stroke of visible) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = stroke.c;
        ctx.lineWidth = stroke.s;
        drawPolyline(ctx, stroke.pts, stroke.pts.length - 1);
    }
};

const animateStroke = async (ctx, stroke, durationMs, signal) => {
    const { pts, c, s } = stroke;
    if (pts.length < 2) {
        ctx.fillStyle = c;
        ctx.beginPath();
        ctx.arc(pts[0][0], pts[0][1], s / 2, 0, Math.PI * 2);
        ctx.fill();
        return;
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = c;
    ctx.lineWidth = s;
    const start = performance.now();
    return new Promise((resolve) => {
        let lastDrawnIndex = 0;
        const tick = (now) => {
            if (signal.cancelled) return resolve();
            const elapsed = now - start;
            const progress = Math.min(1, elapsed / durationMs);
            const targetIndex = Math.floor(progress * (pts.length - 1));
            if (targetIndex > lastDrawnIndex) {
                ctx.beginPath();
                ctx.moveTo(pts[lastDrawnIndex][0], pts[lastDrawnIndex][1]);
                for (let i = lastDrawnIndex + 1; i <= targetIndex; i++) {
                    ctx.lineTo(pts[i][0], pts[i][1]);
                }
                ctx.stroke();
                lastDrawnIndex = targetIndex;
            }
            if (progress < 1) {
                requestAnimationFrame(tick);
            } else {
                resolve();
            }
        };
        requestAnimationFrame(tick);
    });
};

const animateUndo = async (ctx, w, h, visible, signal) => {
    if (visible.length === 0) return;
    const removed = visible[visible.length - 1];
    const remaining = visible.slice(0, -1);
    const start = performance.now();
    return new Promise((resolve) => {
        const tick = (now) => {
            if (signal.cancelled) return resolve();
            const elapsed = now - start;
            const progress = Math.min(1, elapsed / UNDO_MS);
            const upTo = Math.max(0, Math.floor((1 - progress) * (removed.pts.length - 1)));
            ctx.clearRect(0, 0, w, h);
            for (const stroke of remaining) {
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.strokeStyle = stroke.c;
                ctx.lineWidth = stroke.s;
                drawPolyline(ctx, stroke.pts, stroke.pts.length - 1);
            }
            if (upTo > 0) {
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.strokeStyle = removed.c;
                ctx.lineWidth = removed.s;
                drawPolyline(ctx, removed.pts, upTo);
            }
            if (progress < 1) requestAnimationFrame(tick);
            else resolve();
        };
        requestAnimationFrame(tick);
    });
};

const animateClear = async (ctx, w, h, visible, signal) => {
    const start = performance.now();
    return new Promise((resolve) => {
        const tick = (now) => {
            if (signal.cancelled) return resolve();
            const elapsed = now - start;
            const progress = Math.min(1, elapsed / CLEAR_MS);
            // Wipe left-to-right: redraw scene clipped to the un-wiped portion.
            ctx.clearRect(0, 0, w, h);
            const wipeX = progress * w;
            ctx.save();
            ctx.beginPath();
            ctx.rect(wipeX, 0, w - wipeX, h);
            ctx.clip();
            for (const stroke of visible) {
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.strokeStyle = stroke.c;
                ctx.lineWidth = stroke.s;
                drawPolyline(ctx, stroke.pts, stroke.pts.length - 1);
            }
            ctx.restore();
            if (progress < 1) requestAnimationFrame(tick);
            else resolve();
        };
        requestAnimationFrame(tick);
    });
};

const computeAnimatedSchedule = (events, targetMs) => {
    if (events.length === 0) return [];
    const recordingTotalMs = events[events.length - 1].ts || 1;
    const fixedTime = events
        .filter((e) => e.t === 'undo' || e.t === 'clear')
        .reduce((acc, e) => acc + (e.t === 'undo' ? UNDO_MS : CLEAR_MS), 0);
    const pauseTime = Math.max(0, events.length - 1) * EVENT_GAP_MS;
    const flexibleBudget = Math.max(targetMs - fixedTime - pauseTime, 1000);
    // Distribute flexibleBudget across stroke events proportional to their
    // recorded duration. We approximate stroke duration as the gap from this
    // event to the next event; for the last event, fall back to ratio of total.
    const scaleFactor = flexibleBudget / Math.max(recordingTotalMs - fixedTime / 1, 1);
    return events.map((e, i) => {
        if (e.t === 'undo') return { ...e, animMs: UNDO_MS };
        if (e.t === 'clear') return { ...e, animMs: CLEAR_MS };
        // stroke
        const next = events[i + 1];
        const recordedNextTs = next ? next.ts : recordingTotalMs;
        const recordedDuration = Math.max(50, recordedNextTs - e.ts);
        const scaled = recordedDuration * scaleFactor;
        const animMs = Math.max(STROKE_MIN_MS, Math.min(STROKE_MAX_MS, scaled));
        return { ...e, animMs };
    });
};

export const renderStrokes = (canvas, recording, options = {}) => {
    const animated = options.animated !== false;
    const targetMs = options.targetMs ?? 5000;
    const onComplete = options.onComplete;

    const ctx = canvas.getContext('2d');
    const w = recording?.w ?? canvas.width;
    const h = recording?.h ?? canvas.height;
    const events = recording?.events ?? [];

    const signal = { cancelled: false, _cancelHandlers: [], onCancel(fn) { this._cancelHandlers.push(fn); } };
    const cancel = () => {
        signal.cancelled = true;
        signal._cancelHandlers.forEach((fn) => fn());
    };

    const donePromise = (async () => {
        if (!animated) {
            // Snap mode: apply all events synchronously, paint the final scene.
            const visible = [];
            for (const e of events) {
                if (e.t === 'stroke') visible.push(e);
                else if (e.t === 'undo') visible.pop();
                else if (e.t === 'clear') visible.length = 0;
            }
            repaintScene(ctx, w, h, visible);
            if (onComplete) onComplete();
            return;
        }

        const schedule = computeAnimatedSchedule(events, targetMs);
        const visible = [];
        ctx.clearRect(0, 0, w, h);

        let prevEndTs = 0;
        for (let i = 0; i < schedule.length; i++) {
            if (signal.cancelled) break;
            const e = schedule[i];

            // Honor inter-event idle time scaled to the animation budget.
            // We approximate idle by waiting only if the next event's ts in
            // the recording lagged behind the previous event's *natural* end.
            // For simplicity, we just play the events back-to-back; the
            // animation duration of each event already absorbs most of the
            // recording's pacing.

            if (e.t === 'stroke') {
                await animateStroke(ctx, e, e.animMs, signal);
                if (!signal.cancelled) visible.push(e);
            } else if (e.t === 'undo') {
                await animateUndo(ctx, w, h, visible, signal);
                if (!signal.cancelled) visible.pop();
            } else if (e.t === 'clear') {
                await animateClear(ctx, w, h, visible, signal);
                if (!signal.cancelled) visible.length = 0;
            }
            // Brief breath between events so each one reads as a unit.
            if (i < schedule.length - 1 && !signal.cancelled) {
                await sleep(EVENT_GAP_MS, signal);
            }
            prevEndTs = e.ts;
        }

        if (!signal.cancelled && onComplete) onComplete();
    })();

    return { cancel, donePromise };
};
